import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import { trackEvent, EVENTS } from '@/lib/analytics';

// BragBox pricing page, rebuilt 2026-08-24 per the design-system pass
// (_state/bragbox-design-system-2026-08-24.md). Two tiers, one headline,
// one FAQ block. The old offer grid, downsell banner, link stack, urgency
// badge, and strikethrough anchor price were removed as noise.
// Stripe links and the ?plan=&auto=true redirect flow are unchanged.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Currencies stronger than or comparable to USD get local display.
// Everything else defaults to USD.
const SUPPORTED_CURRENCIES = {
  GB: { code: 'GBP', symbol: '£', name: 'British Pound' },
  AU: { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  CA: { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  DE: { code: 'EUR', symbol: '€', name: 'Euro' },
  FR: { code: 'EUR', symbol: '€', name: 'Euro' },
  IE: { code: 'EUR', symbol: '€', name: 'Euro' },
  ES: { code: 'EUR', symbol: '€', name: 'Euro' },
  IT: { code: 'EUR', symbol: '€', name: 'Euro' },
  NL: { code: 'EUR', symbol: '€', name: 'Euro' },
  BE: { code: 'EUR', symbol: '€', name: 'Euro' },
  AT: { code: 'EUR', symbol: '€', name: 'Euro' },
  PT: { code: 'EUR', symbol: '€', name: 'Euro' },
  FI: { code: 'EUR', symbol: '€', name: 'Euro' },
  GR: { code: 'EUR', symbol: '€', name: 'Euro' },
  LU: { code: 'EUR', symbol: '€', name: 'Euro' },
  MT: { code: 'EUR', symbol: '€', name: 'Euro' },
  CY: { code: 'EUR', symbol: '€', name: 'Euro' },
  SK: { code: 'EUR', symbol: '€', name: 'Euro' },
  SI: { code: 'EUR', symbol: '€', name: 'Euro' },
  EE: { code: 'EUR', symbol: '€', name: 'Euro' },
  LV: { code: 'EUR', symbol: '€', name: 'Euro' },
  LT: { code: 'EUR', symbol: '€', name: 'Euro' },
};

const DEFAULT_CURRENCY = { code: 'USD', symbol: '$', name: 'US Dollar' };

const LIFETIME_PRICE = 149;

const freeFeatures = [
  '5 wins per month',
  'Win capture in your own words',
  'Goal tracking',
  'Manual review packets',
];

const lifetimeFeatures = [
  'Unlimited wins, goals, and review packets',
  'AI polish on every win you save',
  'Wins linked to your review objectives',
  'Clean packet export before each review',
  'Every future feature included',
];

const faqs = [
  {
    q: 'What does the free plan include?',
    a: 'Five wins a month, goal tracking, and manual review packets. No credit card at signup. It is enough to try the whole workflow on your own work.',
  },
  {
    q: 'Is lifetime really one payment?',
    a: 'Yes. You pay once, keep access for good, and every feature we add later is included. There is no subscription anywhere on this page.',
  },
  {
    q: 'What if it is not for me?',
    a: 'There is a 30-day money-back guarantee on the lifetime plan. Checkout runs on Stripe and refunds go back to your original payment method.',
  },
  {
    q: 'Is my data private?',
    a: 'Yes. Your wins are encrypted at rest and in transit. We never sell your data and never use it to train AI models. It travels with you when you change employers.',
  },
];

const Pricing = () => {
  const go = useNavigate();
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [userCountry, setUserCountry] = useState(null);

  // Currency detection runs in the background and never gates the page.
  useEffect(() => {
    let cancelled = false;
    const detectCurrency = async () => {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 2500);
        const response = await fetch('https://ipinfo.io/json', {
          headers: { 'Accept': 'application/json' },
          signal: controller.signal,
        });
        clearTimeout(timeout);
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled) return;
        setUserCountry(data.country);
        setCurrency(SUPPORTED_CURRENCIES[data.country] || DEFAULT_CURRENCY);
      } catch (error) {
        // Silent fail. USD default is already set.
      }
    };
    detectCurrency();
    return () => { cancelled = true; };
  }, []);

  // Preserved flow: signup with a paid plan lands here as
  // /pricing?plan=lifetime&auto=true and goes straight to checkout.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const planParam = params.get('plan');
    const autoTrigger = params.get('auto') === 'true';
    if (planParam && autoTrigger) {
      toast.info('Taking you to checkout...');
      setTimeout(() => {
        handleSelectPlan(planParam);
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatPrice = (amount) => {
    if (currency.code === 'EUR') {
      return `€${amount}`;
    }
    return `${currency.symbol}${amount}`;
  };

  const handleSelectPlan = async (planId) => {
    const paymentLinks = {
      monthly: process.env.REACT_APP_STRIPE_MONTHLY_LINK || 'https://buy.stripe.com/9B6dR9adc2I19Vg1d00Ny00',
      yearly: process.env.REACT_APP_STRIPE_YEARLY_LINK || 'https://buy.stripe.com/9B6eVd8546Yh0kGaNA0Ny01',
      lifetime: process.env.REACT_APP_STRIPE_LIFETIME_LINK || 'https://buy.stripe.com/fZu5kD3OO2I17N83l80Ny02',
    };
    const paymentUrl = paymentLinks[planId];
    if (!paymentUrl) {
      toast.error('Payment link not available for this plan');
      return;
    }
    window.location.href = paymentUrl;
  };

  const handleStartFree = () => {
    trackEvent(EVENTS.SIGNUP_STARTED, { source: 'pricing_free_tier', cta: 'start_free', method: 'cta_click' });
    go('/login?signup=true');
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <Helmet>
        <title>Pricing | BragBox</title>
        <meta name="description" content="BragBox is free to start. One payment removes every cap. No subscription, no monthly fee." />
        <link rel="canonical" href="https://bragbox.ai/pricing" />
      </Helmet>

      <SiteHeader source="pricing_nav" />

      {/* Headline */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 text-center">
        <p className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: '#C4A35A' }}>
          PRICING
        </p>
        <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope', color: '#1F2A44' }}>
          Free to start. One payment if you want everything.
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          No subscription and no monthly fee. The free plan is enough to try the
          whole workflow. Lifetime removes the caps.
        </p>
        {userCountry && currency.code !== 'USD' && (
          <p className="text-sm text-gray-500 mt-3">
            Prices shown in {currency.name} ({currency.code})
          </p>
        )}
      </section>

      {/* Two tiers */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Free */}
          <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm flex flex-col">
            <h2 className="text-xl font-bold mb-2" style={{ color: '#1F2A44' }}>Free</h2>
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-4xl font-bold" style={{ fontFamily: 'Manrope', color: '#1F2A44' }}>
                {formatPrice(0)}
              </span>
              <span className="text-gray-500">forever</span>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Enough to build the habit and see the polish work on your own wins.
            </p>
            <ul className="space-y-3 mb-8">
              {freeFeatures.map((feature) => (
                <li key={feature} className="text-sm text-gray-700">{feature}</li>
              ))}
            </ul>
            <Button
              onClick={handleStartFree}
              variant="outline"
              className="w-full py-6 rounded-xl text-base font-semibold mt-auto"
              style={{ borderColor: '#1F2A44', color: '#1F2A44' }}
              data-testid="free-plan-btn"
            >
              Start free
            </Button>
          </div>

          {/* Lifetime */}
          <div className="bg-white p-8 rounded-2xl border-2 shadow-sm flex flex-col" style={{ borderColor: '#C4A35A' }}>
            <h2 className="text-xl font-bold mb-2" style={{ color: '#1F2A44' }}>Lifetime</h2>
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-4xl font-bold" style={{ fontFamily: 'Manrope', color: '#1F2A44' }}>
                {formatPrice(LIFETIME_PRICE)}
              </span>
              <span className="text-gray-500">one payment</span>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              The full workspace, yours for the rest of your career.
            </p>
            <ul className="space-y-3 mb-8">
              {lifetimeFeatures.map((feature) => (
                <li key={feature} className="text-sm text-gray-700">{feature}</li>
              ))}
            </ul>
            <Button
              onClick={() => handleSelectPlan('lifetime')}
              className="w-full py-6 rounded-xl text-base font-semibold mt-auto"
              style={{ backgroundColor: '#1F2A44', color: 'white' }}
              data-testid="lifetime-plan-btn"
            >
              Get lifetime access
            </Button>
            <p className="text-xs text-center text-gray-500 mt-3">
              30-day money-back guarantee. Secure checkout via Stripe.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-10" style={{ fontFamily: 'Manrope', color: '#1F2A44' }}>
          Common questions
        </h2>
        <div className="space-y-4">
          {faqs.map((item) => (
            <details key={item.q} className="border border-gray-200 rounded-xl p-5 group">
              <summary className="font-semibold cursor-pointer list-none flex justify-between items-center" style={{ color: '#1F2A44' }}>
                {item.q}
                <span className="text-gray-400 group-open:rotate-180 transition-all">▾</span>
              </summary>
              <p className="mt-3 text-gray-600 text-sm">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default Pricing;
